import client from '../../shared/api/client';

export type HoldingScope = 'p2p' | 'otc';

export interface HoldingScopeSettings {
  enabled: boolean;
  period_minutes: number;
}

// Top-level enabled/period_minutes mirror the P2P section (legacy shape);
// each section is also exposed under its own key.
export interface HoldingSettings extends HoldingScopeSettings {
  p2p?: HoldingScopeSettings;
  otc?: HoldingScopeSettings;
  updated_at?: string | null;
}

export interface HoldingLockRecord {
  id: number;
  account_id: number;
  user_id: number;
  currency: string;
  amount: number;
  remaining: number;
  source: string;
  reference_id: string;
  expires_at: string;
  released_at: string | null;
  created_at?: string;
}

export interface HoldingSummaryRow {
  user_id: number;
  currency: string;
  locks_count: number;
  frozen: number;
  next_unlock_at: string;
  last_unlock_at: string;
}

interface HoldingBackendResponse<T> {
  success?: boolean;
  data: T;
}

export const getHoldingsSummary = async (source?: HoldingScope): Promise<HoldingSummaryRow[]> => {
  const response = await client.get<HoldingBackendResponse<HoldingSummaryRow[]>>('/holding', {
    params: source ? { source } : {},
  });
  return response.data.data;
};

export const getHoldingSettings = async (): Promise<HoldingSettings> => {
  const response = await client.get<HoldingBackendResponse<HoldingSettings>>('/holding/settings');
  return response.data.data;
};

export const updateHoldingSettings = async (
  payload: Partial<Pick<HoldingSettings, 'enabled' | 'period_minutes'>> & { scope?: HoldingScope }
): Promise<HoldingSettings> => {
  const response = await client.put<HoldingBackendResponse<HoldingSettings>>(
    '/holding/settings',
    payload
  );
  return response.data.data;
};

export const listUserHoldings = async (
  userId: number,
  currency?: string,
  source?: HoldingScope
): Promise<HoldingLockRecord[]> => {
  const response = await client.get<HoldingBackendResponse<HoldingLockRecord[]>>(
    `/holding/user/${userId}`,
    { params: { ...(currency ? { currency } : {}), ...(source ? { source } : {}) } }
  );
  return response.data.data;
};

export const releaseUserHoldings = async (
  userId: number,
  currency?: string
): Promise<{ released: number; message?: string }> => {
  const response = await client.post<{ success?: boolean; released: number; message?: string }>(
    `/holding/user/${userId}/release`,
    currency ? { currency } : {}
  );
  return response.data;
};
